import React, {useEffect, useRef, useState} from 'react'; // Core React hooks
import {InteractionManager, View} from 'react-native';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import KeyboardInput from '/component/base/KeyboardInput';
import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {FieldWrapper} from '../../widgets/FieldWrapper';
import {PhoneInput} from '../../widgets/PhoneInput';
import {SectionTitle} from '../../widgets/SectionTitle';

import {styles} from './ContactSection.style';
import type {ContactSectionProps} from './ContactSection.types';

const {contactSection} = ManageMySiteTestIDs;

export const ContactSection = ({
  name,
  mail,
  primary,
  secondary,
  onNameChange,
  onMailChange,
  onPrimaryChange,
  onSecondaryChange,
  hasSubmitted,
  errors,
  touchedFields,
  onNameBlur,
  onMailBlur,
  onPrimaryBlur,
  onSecondaryBlur,
}: ContactSectionProps) => {
  const DISPLAY_NAME_MAX_LENGTH = 100;
  const EMAIL_MAX_LENGTH = 100;

  const [showSecondary, setShowSecondary] = useState(!!secondary);
  const [pendingFocusTarget, setPendingFocusTarget] = useState<
    'secondaryInput' | 'primaryInput' | null
  >(null);
  const [hideSecondaryDeleteA11y, setHideSecondaryDeleteA11y] = useState(false);

  const nameInputRef = useRef<any>(null);
  const emailInputRef = useRef<any>(null);
  const primaryPhoneRef = useRef<any>(null);
  const secondaryPhoneRef = useRef<any>(null);

  const showNameError = (hasSubmitted || touchedFields?.name) && !!errors.name;
  const showMailError = (hasSubmitted || touchedFields?.mail) && !!errors.mail;
  const showPrimaryError =
    (hasSubmitted || touchedFields?.primary) && !!errors.primary;
  const showSecondaryError =
    (hasSubmitted || touchedFields?.secondary) && !!errors.secondary;

  useEffect(() => {
    setShowSecondary(!!secondary);
  }, [secondary]);

  useEffect(() => {
    if (!pendingFocusTarget) {
      return;
    }

    if (pendingFocusTarget === 'secondaryInput' && showSecondary) {
      const task = InteractionManager.runAfterInteractions(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            secondaryPhoneRef.current?.focusAll?.();

            setTimeout(() => {
              setHideSecondaryDeleteA11y(false);
              setPendingFocusTarget(null);
            }, 250);
          }, 0);
        });
      });

      return () => task.cancel?.();
    }

    if (pendingFocusTarget === 'primaryInput' && !showSecondary) {
      const task = InteractionManager.runAfterInteractions(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            primaryPhoneRef.current?.focusAll?.();
            setPendingFocusTarget(null);
          }, 0);
        });
      });

      return () => task.cancel?.();
    }

    return undefined;
  }, [pendingFocusTarget, showSecondary]);

  const handleAddSecondary = () => {
    setHideSecondaryDeleteA11y(true);
    setPendingFocusTarget('secondaryInput');
    setShowSecondary(true);
  };

  const handleDeleteSecondary = () => {
    setHideSecondaryDeleteA11y(true);
    primaryPhoneRef.current?.focusKeyboard?.();

    setTimeout(() => {
      onSecondaryChange('');
      onSecondaryBlur?.();
      setPendingFocusTarget('primaryInput');
      setShowSecondary(false);
    }, 0);
  };

  const nameAccessibilityLabel = showNameError
    ? name
      ? `${name}, Display Name, required, Error: ${errors.name}`
      : `Display Name, required, Error: ${errors.name}`
    : name
    ? `${name}, Display Name, required`
    : 'Display Name, required';

  const emailAccessibilityLabel = showMailError
    ? mail
      ? `${mail}, Email address, Error: ${errors.mail}`
      : `Email address, Error: ${errors.mail}`
    : mail
    ? `${mail}, Email address`
    : 'Email address';

  const primaryPhoneAccessibilityLabel = showPrimaryError
    ? primary
      ? `${primary}, Primary phone number, Error: ${errors.primary}`
      : `Primary phone number, Error: ${errors.primary}`
    : primary
    ? `${primary}, Primary phone number`
    : 'Primary phone number';

  const secondaryPhoneAccessibilityLabel = showSecondaryError
    ? secondary
      ? `${secondary}, Secondary phone number, Error: ${errors.secondary}`
      : `Secondary phone number, Error: ${errors.secondary}`
    : secondary
    ? `${secondary}, Secondary phone number`
    : 'Secondary phone number';

  return (
    <View
      style={styles.section}
      testID={contactSection.container}
      accessible={false}>
      <SectionTitle
        title={tt('contact', 'Contact Information')}
        accessibilityRole="header"
      />

      <FieldWrapper
        label={String(tt('displayName', 'Display Name'))}
        labelTestID={contactSection.nameLabel}
        errorText={showNameError ? errors.name : undefined}
        errorTestID={contactSection.nameError}
        required>
        <KeyboardInput
          ref={nameInputRef}
          value={name}
          onChangeText={onNameChange}
          onBlur={onNameBlur}
          placeholder="Enter your full name (e.g. Jane Doe)"
          placeholderTextColor="#9CA3AF"
          accessible
          accessibilityRole="none"
          accessibilityLabel={nameAccessibilityLabel}
          accessibilityHint="Editable"
          accessibilityValue={{text: name || 'Empty'}}
          accessibilityState={showNameError ? {invalid: true} : undefined}
          testID={contactSection.nameInput}
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          style={[styles.input, showNameError ? styles.inputError : null]}
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </FieldWrapper>

      <FieldWrapper
        label={String(tt('emailAddress', 'Email Address'))}
        labelTestID={contactSection.emailLabel}
        errorText={showMailError ? errors.mail : undefined}
        errorTestID={contactSection.emailError}>
        <KeyboardInput
          ref={emailInputRef}
          value={mail}
          onChangeText={onMailChange}
          onBlur={onMailBlur}
          placeholder="Enter Email address"
          placeholderTextColor="#9CA3AF"
          keyboardType="email-address"
          autoCapitalize="none"
          accessible
          accessibilityRole="none"
          accessibilityLabel={emailAccessibilityLabel}
          accessibilityHint="Editable"
          accessibilityValue={{text: mail || 'Empty'}}
          accessibilityState={showMailError ? {invalid: true} : undefined}
          testID={contactSection.emailInput}
          maxLength={EMAIL_MAX_LENGTH}
          style={[styles.input, showMailError ? styles.inputError : null]}
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </FieldWrapper>

      <FieldWrapper
        label={String(tt('phonePrimary', 'Phone Number - Primary'))}
        labelTestID={contactSection.primaryPhoneLabel}>
        <PhoneInput
          ref={primaryPhoneRef}
          value={primary}
          onChange={onPrimaryChange}
          onBlurValue={onPrimaryBlur}
          hasError={showPrimaryError}
          errorText={errors.primary}
          testID={contactSection.primaryPhoneField}
          accessibilityLabel={primaryPhoneAccessibilityLabel}
          accessibilityHint="Editable"
        />
      </FieldWrapper>

      {showSecondary ? (
        <FieldWrapper
          label={String(tt('phoneSecondary', 'Phone Number - Secondary'))}
          labelTestID={contactSection.secondaryPhoneLabel}>
          <PhoneInput
            ref={secondaryPhoneRef}
            value={secondary}
            onChange={onSecondaryChange}
            onBlurValue={onSecondaryBlur}
            hasError={showSecondaryError}
            errorText={errors.secondary}
            onDelete={handleDeleteSecondary}
            hideDeleteFromAccessibility={hideSecondaryDeleteA11y}
            testID={contactSection.secondaryPhoneField}
            accessibilityLabel={secondaryPhoneAccessibilityLabel}
            accessibilityHint="Editable"
          />
        </FieldWrapper>
      ) : (
        <KeyboardFocusable
          testID={contactSection.addSecondaryButton}
          accessible
          accessibilityRole="button"
          accessibilityLabel="Add secondary phone number"
          accessibilityHint="Double tap to add an optional secondary phone number field"
          onPress={handleAddSecondary}
          style={styles.addPhoneBtn}>
          <View style={styles.addPhoneBtnContent} accessible={false}>
            <Icon
              name="plus"
              style={styles.plusIcon}
              accessible={false}
              importantForAccessibility="no-hide-descendants"
            />
            <Text
              style={styles.addPhoneBtnText}
              allowFontScaling
              maxFontSizeMultiplier={2}>
              {tt('addSecondaryPhone', 'Add Secondary Phone')}
            </Text>
          </View>
        </KeyboardFocusable>
      )}
    </View>
  );
};

export default ContactSection;
